import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { INITIAL_LOADING, initialLoading, SELECT_OFFICE, SIGN_IN_ACTION } from '~/actions';
import { createLoadingSelector } from '~/api/selectors';
import { useAppSelector, useIsSuccess } from '~/hooks';

const loadingSelector = createLoadingSelector([INITIAL_LOADING, SIGN_IN_ACTION, SELECT_OFFICE]);

/**
 * Custom hook to manage the initial loading state of the application.
 * Handles dispatching initial loading actions.
 */
export const useInitialLoading = () => {
  const dispatch = useDispatch();
  const refreshToken = localStorage.getItem('refresh_token_dental');
  const hasRefreshToken = refreshToken != null && refreshToken !== '';

  // Check if SIGN_IN_ACTION or INITIAL_LOADING actions have succeeded
  const { isSuccess: isSuccessSignInAction } = useIsSuccess([SIGN_IN_ACTION]);
  const { isSuccess: isSuccessInitialLoadingAction } = useIsSuccess([INITIAL_LOADING]);

  // State to track if this is the first loading
  const [isFirstLoading, setIsFirstLoading] = useState(hasRefreshToken);
  const isFirstRenderRef = useRef(true);

  // Selector to check if any loading actions are in progress
  const isLoadingActions = useAppSelector((state) => loadingSelector(state));

  // Effect to trigger initial loading on first load if refreshToken exists
  useEffect(() => {
    if (hasRefreshToken) {
      setIsFirstLoading(true);
    } else {
      setIsFirstLoading(false);
    }
  }, [hasRefreshToken, dispatch, isSuccessSignInAction]);

  // Effect to dispatch initial loading
  useEffect(() => {
    if (hasRefreshToken) {
      dispatch(initialLoading());
    }
  }, [dispatch, hasRefreshToken, isSuccessSignInAction]);

  // Effect to reset loading state after initial loading action succeeds
  useEffect(() => {
    if (isSuccessInitialLoadingAction && !isFirstRenderRef.current) {
      setIsFirstLoading(false);
    }
    isFirstRenderRef.current = false;
  }, [isSuccessInitialLoadingAction]);

  return {
    isLoading: isFirstLoading || isLoadingActions,
  };
};
